import React, { useEffect, useRef, useState } from "react";
import {
  FaRegImages,
  FaCamera,
  FaMagic,
  FaMicrophone,
  FaRegFileAudio,
  FaPlay,
  FaPause,
} from "react-icons/fa";
import "./CreatePanel.css";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../supabase/supabase";


// Convert an audio Blob/File to 16‑bit PCM WAV for maximum compatibility (mobile expects .wav)
async function convertBlobToWavFile(blob) {
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const wavBuffer = encodeWavPCM16(audioBuffer);
    const wavBlob = new Blob([wavBuffer], { type: "audio/wav" });
    const fileName = `audio_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.wav`;
    return new File([wavBlob], fileName, { type: "audio/wav" });
  } catch {
    const fallbackName = `audio_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.wav`;
    return new File([blob], fallbackName, { type: "audio/wav" });
  }
}

function encodeWavPCM16(audioBuffer) {
  const numOfChan = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const samples = audioBuffer.length;
  const bytesPerSample = 2;
  const blockAlign = numOfChan * bytesPerSample;
  const buffer = new ArrayBuffer(44 + samples * blockAlign);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + samples * blockAlign, true);
  writeString(view, 8, "WAVE");

  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numOfChan, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 8 * bytesPerSample, true);

  writeString(view, 36, "data");
  view.setUint32(40, samples * blockAlign, true);

  const channels = [];
  for (let i = 0; i < numOfChan; i++) channels.push(audioBuffer.getChannelData(i));
  let offset = 44;
  for (let i = 0; i < samples; i++) {
    for (let c = 0; c < numOfChan; c++) {
      let sample = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }
  return buffer;
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) view.setUint8(offset + i, string.charCodeAt(i));
}


// Helper function to get thumbnail URL for a link (same logic as Feed.jsx)
const getLinkThumbnailUrl = (url) => {
  if (!url) return "/default-link-thumb.png";
  
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    let videoId = null;

    // YouTube URL patterns
    if (host === "youtube.com" || host === "m.youtube.com" || host.endsWith("youtube.com")) {
      // YouTube Shorts (youtube.com/shorts/VIDEO_ID)
      const shortsMatch = u.pathname.match(/^\/shorts\/([^\/\?]+)/);
      if (shortsMatch) {
        videoId = shortsMatch[1];
      } 
      // YouTube Live (youtube.com/live/VIDEO_ID)
      else if (u.pathname.startsWith('/live/')) {
        const liveMatch = u.pathname.match(/^\/live\/([^\/\?]+)/);
        if (liveMatch) {
          videoId = liveMatch[1];
        }
      }
      // YouTube Embed (youtube.com/embed/VIDEO_ID)
      else if (u.pathname.startsWith('/embed/')) {
        const embedMatch = u.pathname.match(/^\/embed\/([^\/\?]+)/);
        if (embedMatch) {
          videoId = embedMatch[1];
        }
      }
      // Regular YouTube video (?v=VIDEO_ID)
      else {
        videoId = u.searchParams.get("v");
      }
    } 
    // Short youtu.be links (youtu.be/VIDEO_ID)
    else if (host === "youtu.be") {
      videoId = u.pathname.slice(1).split('?')[0]; // Remove query params if any
    }

    if (videoId) {
      // Clean video ID (remove any query params or fragments)
      videoId = videoId.split('?')[0].split('#')[0].trim();
      
      // Use hqdefault.jpg for better quality, with maxresdefault as preferred
      // The component will fallback to mqdefault if needed
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
  } catch (err) {
    console.warn('[CreatePanel] Error parsing URL:', err);
  }

  // Instagram Reels/Posts - will be fetched asynchronously
  const igReel = url.match(/instagram\.com\/(?:reel|p|tv)\/([^\/?]+)/);
  if (igReel) {
    // Return null to trigger async fetch
    return null;
  }

  // TikTok - will be fetched asynchronously
  const ttPatterns = [
    /tiktok\.com\/@[^\/]+\/video\/(\d+)/,
    /vm\.tiktok\.com\/[^\/]+/,
    /vt\.tiktok\.com\/[^\/]+/,
    /www\.tiktok\.com\/t\/[^\/]+/,
    /tiktok\.com\/t\/[^\/]+/
  ];

  const isTikTok = ttPatterns.some(pattern => pattern.test(url));
  if (isTikTok) {
    // Return null to trigger async fetch
    return null;
  }

  // Facebook - detect all types (share links, watch videos, posts, etc.)
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "facebook.com" || host === "m.facebook.com" || host.endsWith("facebook.com")) {
      // Check for various Facebook link patterns
      const fbPatterns = [
        /facebook\.com\/watch\/\?v=(\d+)/,           // Watch videos
        /facebook\.com\/share\/r\/[^\/]+/,           // Share links
        /facebook\.com\/.*\/videos\/\d+/,            // Video posts
        /facebook\.com\/.*\/posts\/[^\/]+/,          // Posts
        /facebook\.com\/.*\/permalink\/\d+/,         // Permalink posts
        /facebook\.com\/.*\/photos\/[^\/]+/,         // Photo posts
        /facebook\.com\/.*\/reel\/[^\/]+/,           // Reels
      ];
      
      const isFacebook = fbPatterns.some(pattern => pattern.test(url));
      if (isFacebook) {
        // Return null to trigger async fetch for all Facebook links
        return null;
      }
    }
  } catch (err) {
    // Invalid URL, fall through
  }

  // Fallback
  return "/default-link-thumb.png";
};

// Backend API URL - adjust if your backend runs on a different port/domain
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL;

// Unified function to fetch link thumbnail via backend (avoids CORS)
const fetchLinkThumbnail = async (url) => {
  try {
    console.log('[CreatePanel] Fetching thumbnail for:', url);
    const backendUrl = `${BACKEND_API_URL}/api/fetch-link-thumbnail?url=${encodeURIComponent(url)}`;
    console.log('[CreatePanel] Backend URL:', backendUrl);
    
    const response = await fetch(backendUrl);
    
    console.log('[CreatePanel] Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[CreatePanel] Backend error response:', errorText);
      throw new Error(`Failed to fetch thumbnail: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('[CreatePanel] Backend response data:', data);
    
    // Return thumbnail URL if available, otherwise return default
    if (data.thumbnail_url) {
      console.log('[CreatePanel] Using thumbnail URL:', data.thumbnail_url);
      return data.thumbnail_url;
    }
    
    // Return default thumbnail based on link type
    console.log('[CreatePanel] No thumbnail URL, using default:', data.default);
    return data.default || "/default-link-thumb.png";
  } catch (error) {
    console.error('[CreatePanel] Thumbnail fetch error:', error);
    console.error('[CreatePanel] Error details:', error.message, error.stack);
    
    // Fallback: determine default based on URL
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('facebook.com')) {
      return "/default-facebook-thumb2.png";
    } else if (lowerUrl.includes('instagram.com')) {
      return "/default-instagram-thumb.jpeg";
    } else if (lowerUrl.includes('tiktok.com')) {
      return "/default-tiktok-thumb.jpeg";
    }
    
    return "/default-link-thumb.png";
  }
};

// Keep these for backward compatibility, but they now use the unified function
const fetchInstagramThumbnail = (url) => fetchLinkThumbnail(url);
const fetchTikTokThumbnail = (url) => fetchLinkThumbnail(url);
const fetchFacebookThumbnail = (url) => fetchLinkThumbnail(url);

// Helper function to save thumbnail from URL to storage
const saveThumbnailFromUrl = async (thumbnailUrl, userId, postId) => {
  try {
    if (!thumbnailUrl || thumbnailUrl.startsWith("/default-")) {
      return; // Skip default placeholders
    }
    
    const response = await fetch(thumbnailUrl);
    if (!response.ok) {
      console.warn("Failed to fetch thumbnail from URL:", thumbnailUrl);
      return;
    }
    
    const blob = await response.blob();
    const thumbnailFile = new File([blob], `thumbnail.jpg`, { type: "image/jpeg" });
    
    // Save thumbnail to storage: {userId}/{postId}_thumb.jpg
    const thumbnailPath = `${userId}/${postId}_thumb.jpg`;
    const { error: thumbError } = await supabase.storage
      .from("post-media")
      .upload(thumbnailPath, thumbnailFile, {
        cacheControl: "3600",
        upsert: true, // Allow overwrite if exists
        contentType: "image/jpeg",
      });
    
    if (thumbError) {
      console.warn("Failed to save thumbnail to storage:", thumbError);
    } else {
      console.log("Thumbnail saved successfully:", thumbnailPath);
    }
  } catch (err) {
    console.warn("Error saving thumbnail from URL:", err);
  }
};

export default function CreatePanel({ userProfile, onContentChange, onClearContent }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [media, setMedia] = useState([]);
  const [textContent, setTextContent] = useState("");
  const [linkUrl, setLinkUrl] = useState(null);
  const [linkThumbnail, setLinkThumbnail] = useState(null);
  const inputRef = useRef(null);
  const lastSavedLinkUrlRef = useRef(null);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const musicInputRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [placeholderVisible, setPlaceholderVisible] = useState(true);
  const { translations: tRaw } = useLanguage();
  const t = { ...tRaw, create: { ...tRaw?.create } };
  const { currentUser: authUser } = useAuth();

  // Use the userProfile prop passed from Feed component
  useEffect(() => {
    if (userProfile) {
      setCurrentUser({
        username: userProfile.username || userProfile.display_name || "User",
        profileImage: userProfile.avatar_url || "/default-avatar.png"
      });
    }
  }, [userProfile]);

// Load saved content on component mount
useEffect(() => {
  const savedContent = localStorage.getItem("createPanelContent");
  if (savedContent && savedContent !== "null") {
    try {
      const parsed = JSON.parse(savedContent);
      if (parsed.text) {
        setTextContent(parsed.text);
        setPlaceholderVisible(false);
        if (inputRef.current) {
          inputRef.current.textContent = parsed.text;
        }
      }
      if (parsed.media && Array.isArray(parsed.media)) {
        setMedia(parsed.media);
      }
      if (parsed.linkUrl) {
        setLinkUrl(parsed.linkUrl);
        const thumbUrl = getLinkThumbnailUrl(parsed.linkUrl);
        if (thumbUrl === null) {
          // Need to fetch thumbnail asynchronously (Instagram, TikTok, or Facebook)
          const url = parsed.linkUrl.toLowerCase();
          if (url.includes('instagram.com')) {
            fetchInstagramThumbnail(parsed.linkUrl).then(setLinkThumbnail);
          } else if (url.includes('tiktok.com')) {
            fetchTikTokThumbnail(parsed.linkUrl).then(setLinkThumbnail);
          } else if (url.includes('facebook.com')) {
            fetchFacebookThumbnail(parsed.linkUrl).then(setLinkThumbnail);
          } else {
            setLinkThumbnail("/default-link-thumb.png");
          }
        } else if (thumbUrl.startsWith("/default-")) {
          // If it's a default thumbnail, check if we should try fetching
          const url = parsed.linkUrl.toLowerCase();
          if (url.includes('facebook.com')) {
            // Try fetching Facebook thumbnail even if default was returned
            fetchFacebookThumbnail(parsed.linkUrl).then(setLinkThumbnail);
          } else {
            setLinkThumbnail(thumbUrl);
          }
        } else {
          setLinkThumbnail(thumbUrl);
        }
      }
    } catch (error) {
      console.error("Error loading saved content:", error);
    }
  }
}, []);

// Detect and extract link from text content
useEffect(() => {
  const URL_REGEX = /https?:\/\/[^\s]+/g;
  const matches = textContent.match(URL_REGEX);
  
  if (matches && matches.length > 0) {
    const firstUrl = matches[0];
    if (firstUrl !== linkUrl) {
      setLinkUrl(firstUrl);
      const thumbUrl = getLinkThumbnailUrl(firstUrl);
      if (thumbUrl === null) {
        // Need to fetch thumbnail asynchronously (Instagram, TikTok, or Facebook)
        const url = firstUrl.toLowerCase();
        if (url.includes('instagram.com')) {
          fetchInstagramThumbnail(firstUrl).then(setLinkThumbnail);
        } else if (url.includes('tiktok.com')) {
          fetchTikTokThumbnail(firstUrl).then(setLinkThumbnail);
        } else if (url.includes('facebook.com')) {
          fetchFacebookThumbnail(firstUrl).then(setLinkThumbnail);
        } else {
          setLinkThumbnail("/default-link-thumb.png");
        }
      } else if (thumbUrl.startsWith("/default-")) {
        // If it's a default thumbnail, check if we should try fetching
        const url = firstUrl.toLowerCase();
        if (url.includes('facebook.com')) {
          // Try fetching Facebook thumbnail even if default was returned
          fetchFacebookThumbnail(firstUrl).then(setLinkThumbnail);
        } else {
          setLinkThumbnail(thumbUrl);
        }
      } else {
        setLinkThumbnail(thumbUrl);
      }
    }
  } else {
    if (linkUrl) {
      setLinkUrl(null);
      setLinkThumbnail(null);
    }
  }
}, [textContent, linkUrl]);

// Save content when linkUrl changes (separate from text input)
useEffect(() => {
  // Only save if linkUrl actually changed and we have a callback
  if (linkUrl !== lastSavedLinkUrlRef.current && onContentChange) {
    lastSavedLinkUrlRef.current = linkUrl;
    onContentChange({
      text: textContent,
      media: media,
      linkUrl: linkUrl
    });
  } else if (!linkUrl && lastSavedLinkUrlRef.current) {
    // Link was removed
    lastSavedLinkUrlRef.current = null;
    if (onContentChange) {
      onContentChange({
        text: textContent,
        media: media,
        linkUrl: null
      });
    }
  }
}, [linkUrl, textContent, media, onContentChange]);


  const handlePhotoClick = () => photoInputRef.current.click();
  const handleMusicClick = () => musicInputRef.current.click();
  const handleCameraClick = () => cameraInputRef.current.click();

  const handleMediaChange = (e, type) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const created = files.map((file) => {
      const detected =
        file.type?.startsWith("video/") ? "video" :
        file.type?.startsWith("image/") ? "image" :
        type;
      return {
        url: URL.createObjectURL(file),
        type: detected,
        file,
      };
    });

    const newMedia = [...media, ...created];
    setMedia(newMedia);

    if (onContentChange) {
      onContentChange({
        text: textContent,
        media: newMedia,
      });
    }

    // clear input so selecting the same files again still triggers change
    e.target.value = "";
  };

  // Timer effect for recording duration
  useEffect(() => {
    let interval = null;
    if (recording) {
      setRecordingDuration(0);
      interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } else {
      setRecordingDuration(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [recording]);

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVoiceClick = async () => {
    // Utility to pick a supported mimeType
    const getSupportedMimeType = () => {
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      for (const type of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(type)) return type;
      }
      return "";
    };

    if (!recording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 48000,
          },
        });

        const mimeType = getSupportedMimeType();
        const recorder = new MediaRecorder(stream, {
          ...(mimeType ? { mimeType } : {}),
          audioBitsPerSecond: 128000,
        });

        const chunks = [];
        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) chunks.push(e.data);
        };
        recorder.onstop = async () => {
          try {
            const blob = new Blob(chunks, { type: mimeType || "audio/webm" });
            const wavFile = await convertBlobToWavFile(blob);
            const url = URL.createObjectURL(wavFile);
            const newMedia = [...media, { url, type: "audio", file: wavFile }];
            setMedia(newMedia);
            if (onContentChange) {
              onContentChange({ text: textContent, media: newMedia });
            }
          } finally {
            try { stream.getTracks().forEach((t) => t.stop()); } catch {}
            setMediaStream(null);
          }
        };

        recorder.start(250); // small timeslice to avoid cut/delay issues
        setMediaRecorder(recorder);
        setMediaStream(stream);
        setRecording(true);
      } catch (err) {
        alert("Microphone access denied.");
      }
    } else {
      try { mediaRecorder && mediaRecorder.stop(); } catch {}
      try { mediaStream && mediaStream.getTracks().forEach((t) => t.stop()); } catch {}
      setRecording(false);
    }
  };

  const handleUpload = async () => {
    if (uploading) return;
    const userId = authUser?.id;
    if (!userId) {
      alert("Please sign in to upload.");
      return;
    }
    
    // Build caption: include user text and link URL if present
    let captionStr = (textContent || "").trim();
    
    // If we have a detected linkUrl that's not already in the caption, add it
    if (linkUrl) {
      const URL_REGEX = /https?:\/\/[^\s]+/g;
      const hasLinkInCaption = captionStr.match(URL_REGEX);
      
      // If link is not in caption, append it
      if (!hasLinkInCaption || !hasLinkInCaption.includes(linkUrl)) {
        // Add link to caption with proper spacing
        if (captionStr.length > 0) {
          captionStr = `${captionStr} ${linkUrl}`;
        } else {
          captionStr = linkUrl;
        }
      }
    }
    
    const itemsWithFiles = media.filter((m) => m?.file instanceof Blob || m?.file instanceof File);
    
    // Validate: need at least caption, media, or link
    if (itemsWithFiles.length === 0 && captionStr.length === 0) {
      alert("Add a photo, voice note, link, or caption before uploading.");
      return;
    }
    
    setUploading(true);
    try {
      const prefix = Date.now().toString();
      let mediaIndex = 0;
      const uploadedNames = [];
      let uploadedAudioName = null;

      for (const item of itemsWithFiles) {
        let file = item.file;
        const original = file.name || "";
        const rawExtFromName = original.includes(".") ? original.split(".").pop().toLowerCase() : "";
        const rawExtFromType = (file.type && file.type.split("/")[1]) || "";
        let ext = rawExtFromName || rawExtFromType || "bin";
        if (ext === "jpeg") ext = "jpg";
        if (ext === "quicktime") ext = "mov";
        if (ext === "x-m4a") ext = "m4a";

        // Ensure audio is .wav
        if (item.type === "audio" && file.type !== "audio/wav") {
          file = await convertBlobToWavFile(file);
          ext = "wav";
        }

        // Use _0, _1, ... convention; audio index follows images
        const indexForThis = item.type === "audio" ? mediaIndex : mediaIndex;
        const name = `${prefix}_${indexForThis}.${item.type === "audio" ? "wav" : ext}`;
        const path = `${userId}/${name}`;
        const { error } = await supabase.storage.from("post-media").upload(path, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type || undefined,
        });
        if (error) throw error;

        if (item.type === "audio") {
          uploadedAudioName = name;
        } else {
          uploadedNames.push(name);
          mediaIndex += 1;
        }
      }

      // Insert post with link in caption (no separate link_url column)
      const { data: insertedPost, error: insertError } = await supabase.from("posts").insert({
        user_id: userId,
        caption: captionStr || "",
        media_names: uploadedNames.length > 0 ? uploadedNames : null,
        audio_filename: uploadedAudioName || null,
        created_at: new Date().toISOString(),
      }).select().single();
      
      if (insertError) throw insertError;
      
      // If there's a link, fetch and save the thumbnail
      if (linkUrl && insertedPost?.id) {
        try {
          let thumbnailUrl = getLinkThumbnailUrl(linkUrl);
          
          // For Instagram/TikTok/Facebook, fetch thumbnail asynchronously
          if (thumbnailUrl === null || thumbnailUrl.startsWith("/default-")) {
            const url = linkUrl.toLowerCase();
            if (url.includes('instagram.com')) {
              thumbnailUrl = await fetchInstagramThumbnail(linkUrl);
            } else if (url.includes('tiktok.com')) {
              thumbnailUrl = await fetchTikTokThumbnail(linkUrl);
            } else if (url.includes('facebook.com')) {
              thumbnailUrl = await fetchFacebookThumbnail(linkUrl);
            }
          }
          
          // Save thumbnail if we have a valid URL
          if (thumbnailUrl && !thumbnailUrl.startsWith("/default-")) {
            await saveThumbnailFromUrl(thumbnailUrl, userId, insertedPost.id);
          }
        } catch (thumbErr) {
          console.warn("Error saving thumbnail:", thumbErr);
          // Don't throw - post is already created, thumbnail is optional
        }
      }

      // Reset UI
      setMedia([]);
      setTextContent("");
      setLinkUrl(null);
      setLinkThumbnail(null);
      if (inputRef.current) inputRef.current.textContent = "";
      if (onClearContent) onClearContent();
      alert("Post uploaded!");
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleInput = () => {
    const content = inputRef.current.textContent;
    const isEmpty = content.trim() === "";
    setPlaceholderVisible(isEmpty);
    setTextContent(content);
    
    // Check if content is completely empty (no text, no media, no link)
    const isCompletelyEmpty = isEmpty && media.length === 0 && !linkUrl;
    
    if (isCompletelyEmpty) {
      // Clear saved content when everything is empty
      if (onClearContent) {
        onClearContent();
      }
    } else {
      // Save content whenever it changes
      if (onContentChange) {
        onContentChange({
          text: content,
          media: media,
          linkUrl: linkUrl
        });
      }
    }
  };

  const removeMedia = (index) => {
    const newMedia = media.filter((_, i) => i !== index);
    setMedia(newMedia);
    
    // Check if content is completely empty after removing media
    const isCompletelyEmpty = textContent.trim() === "" && newMedia.length === 0 && !linkUrl;
    
    if (isCompletelyEmpty) {
      // Clear saved content when everything is empty
      if (onClearContent) {
        onClearContent();
      }
    } else {
      // Save content when media is removed
      if (onContentChange) {
        onContentChange({
          text: textContent,
          media: newMedia,
          linkUrl: linkUrl
        });
      }
    }
  };

  const removeLink = () => {
    setLinkUrl(null);
    setLinkThumbnail(null);
    
    // Remove link from text content
    const URL_REGEX = /https?:\/\/[^\s]+/g;
    const cleanedText = textContent.replace(URL_REGEX, "").trim();
    setTextContent(cleanedText);
    if (inputRef.current) {
      inputRef.current.textContent = cleanedText;
    }
    setPlaceholderVisible(cleanedText === "");
    
    // Check if content is completely empty after removing link
    const isCompletelyEmpty = cleanedText.trim() === "" && media.length === 0;
    
    if (isCompletelyEmpty) {
      if (onClearContent) {
        onClearContent();
      }
    } else {
      if (onContentChange) {
        onContentChange({
          text: cleanedText,
          media: media,
          linkUrl: null
        });
      }
    }
  };

  return (
    <div className="create-panel">
      <div className="create-header">
        <div className="username-section">
         <img
  src={currentUser?.profileImage || "/default-avatar.png"}
  alt={currentUser?.username}
  className="create-avatar"
/>
<span className="create-username">@{currentUser?.username}</span>

        </div>
        <button 
          className="create-upload-btn"
          onClick={handleUpload}
          disabled={uploading}
        >
          {uploading ? (t.create?.uploading || "Uploading...") : (t.create?.upload || "Upload")}
        </button>
      </div>

      <div className="create-input-wrapper">
  {placeholderVisible && (
    <div className="placeholder-text"> {t.create?.placeholder || "What’s on your mind?"}</div>
  )}
  <div
    className="create-input-component"
    ref={inputRef}
    contentEditable
    onInput={handleInput}
    suppressContentEditableWarning={true}
  />
</div>

      <div className="media-preview">
        {media.map((item, index) => (
          <div
            key={index}
            className={`media-card ${item.type === "audio" ? "audio-card" : ""}`}
          >
            <button className="remove-media" onClick={() => removeMedia(index)}>×</button>
            {item.type === "image" && (
              <img src={item.url} alt="preview" className="media-thumb" />
            )}
            {item.type === "video" && (
              <video src={item.url} className="media-thumb" controls />
            )}
            {item.type === "audio" && (
              <InlineAudioPlayer src={item.url} index={index} />
            )}
          </div>
        ))}
        
        {/* Link Preview */}
        {linkUrl && linkThumbnail && (
          <div className="media-card link-card">
            <button className="remove-media" onClick={removeLink}>×</button>
            <div
              className="link-thumb-container"
              onClick={(e) => {
                e.stopPropagation();
                // Try to open in app on mobile, fallback to browser
                try {
                  const url = new URL(linkUrl);
                  const host = url.hostname.replace(/^www\./, "");
                  
                  // YouTube - try to open in app
                  if (host === "youtu.be" || host.includes("youtube.com")) {
                    const videoId = host === "youtu.be" 
                      ? url.pathname.slice(1)
                      : url.searchParams.get("v");
                    if (videoId) {
                      // Try YouTube app deep link
                      const youtubeAppUrl = `vnd.youtube:${videoId}`;
                      const youtubeWebUrl = `https://www.youtube.com/watch?v=${videoId}`;
                      window.location.href = youtubeAppUrl;
                      // Fallback to web if app doesn't open
                      setTimeout(() => {
                        window.open(youtubeWebUrl, "_blank", "noopener,noreferrer");
                      }, 500);
                      return;
                    }
                  }
                  
                  // Instagram - try to open in app
                  if (host.includes("instagram.com")) {
                    const instagramAppUrl = linkUrl.replace("https://", "instagram://");
                    window.location.href = instagramAppUrl;
                    setTimeout(() => {
                      window.open(linkUrl, "_blank", "noopener,noreferrer");
                    }, 500);
                    return;
                  }
                  
                  // TikTok - try to open in app
                  if (host.includes("tiktok.com")) {
                    const tiktokAppUrl = linkUrl.replace("https://", "snssdk1233://");
                    window.location.href = tiktokAppUrl;
                    setTimeout(() => {
                      window.open(linkUrl, "_blank", "noopener,noreferrer");
                    }, 500);
                    return;
                  }
                  
                  // Default: open in browser
                  window.open(linkUrl, "_blank", "noopener,noreferrer");
                } catch {
                  // Fallback if URL parsing fails
                  window.open(linkUrl, "_blank", "noopener,noreferrer");
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <img 
                src={linkThumbnail} 
                alt="Link thumbnail" 
                className="link-thumb"
                onError={(e) => {
                  // Prevent infinite loop
                  if (e.target.src.includes('default-')) return;
                  
                  // Check link type and use appropriate default thumbnail
                  if (!linkUrl) {
                    e.target.src = "/default-link-thumb.png";
                    return;
                  }
                  
                  const url = linkUrl.toLowerCase();
                  
                  // Try different YouTube thumbnail qualities before falling back to default
                  if (url.includes('youtube.com') || url.includes('youtu.be')) {
                    // Extract video ID and try mqdefault (medium quality) as fallback
                    try {
                      const u = new URL(linkUrl);
                      const host = u.hostname.replace(/^www\./, "");
                      let videoId = null;
                      
                      if (host === "youtube.com" || host === "m.youtube.com" || host.endsWith("youtube.com")) {
                        const shortsMatch = u.pathname.match(/^\/shorts\/([^\/\?]+)/);
                        const liveMatch = u.pathname.match(/^\/live\/([^\/\?]+)/);
                        const embedMatch = u.pathname.match(/^\/embed\/([^\/\?]+)/);
                        
                        if (shortsMatch) {
                          videoId = shortsMatch[1];
                        } else if (liveMatch) {
                          videoId = liveMatch[1];
                        } else if (embedMatch) {
                          videoId = embedMatch[1];
                        } else {
                          videoId = u.searchParams.get("v");
                        }
                      } else if (host === "youtu.be") {
                        videoId = u.pathname.slice(1).split('?')[0];
                      }
                      
                      if (videoId) {
                        videoId = videoId.split('?')[0].split('#')[0].trim();
                        // Try mqdefault (medium quality) as fallback
                        e.target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                        return;
                      }
                    } catch (err) {
                      // Fall through to default
                    }
                    
                    // If still fails, use YouTube Shorts default
                    if (url.includes('/shorts/') || url.includes('youtube.com/shorts')) {
                      e.target.src = "/default-youtubeshorts-thumb.jpeg";
                    } else {
                      e.target.src = "/default-youtubeshorts-thumb.jpeg";
                    }
                  } else if (url.includes('instagram.com')) {
                    e.target.src = "/default-instagram-thumb.jpeg";
                  } else if (url.includes('tiktok.com')) {
                    e.target.src = "/default-tiktok-thumb.jpeg";
                  } else if (url.includes('facebook.com')) {
                    e.target.src = "/default-facebook-thumb2.png";
                  } else {
                    e.target.src = "/default-link-thumb.png";
                  }
                }}
              />
              <div className="link-overlay">
                <span className="link-url-preview">{linkUrl}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="create-icons">
        <div className="tooltip-wrapper" title={t.create?.photo || "Photo"}>
          <FaRegImages  className="create-icon" onClick={handlePhotoClick} />
          <input
            type="file"
            accept="image/*,video/*"
            ref={photoInputRef}
            style={{ display: "none" }}
            multiple
            onChange={(e) => handleMediaChange(e, "image")}
          />
        </div>

        <div className="tooltip-wrapper" title={t.create?.camera || "Camera"}>
          <FaCamera className="create-icon" onClick={handleCameraClick} />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            style={{ display: "none" }}
            onChange={(e) => handleMediaChange(e, "image")}
          />
        </div>

        {/* <div className="tooltip-wrapper" title={t.create?.aiAssistant || "AI Assistant"}>
          <FaMagic className="create-icon" />
        </div> */}

        <div className="tooltip-wrapper" title={t.create?.voice || "Voice"}>
  <FaMicrophone
    className={`create-icon ${recording ? "recording" : ""}`}
    onClick={handleVoiceClick}
  />
</div>

        {/* Recording UI Overlay */}
        {recording && (
          <div className="recording-overlay">
            <div className="recording-indicator">
              <div className="recording-dot"></div>
              <span className="recording-timer">{formatRecordingTime(recordingDuration)}</span>
            </div>
            <button 
              className="stop-recording-btn" 
              onClick={handleVoiceClick}
              aria-label="Stop recording"
            >
              Stop
            </button>
          </div>
        )}


        <div className="tooltip-wrapper" title={t.create?.music || "Music"}>
          <FaRegFileAudio className="create-icon" onClick={handleMusicClick} />
          <input
            type="file"
            accept="audio/*"
            ref={musicInputRef}
            style={{ display: "none" }}
            onChange={(e) => handleMediaChange(e, "audio")}
          />
        </div>
      </div>

      
    </div>
  );
}

function InlineAudioPlayer({ src, index }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const formattedTime = (time) => {
    if (!Number.isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const fixDurationIfNeeded = () => {
      // Some MediaRecorder blobs report duration as Infinity or 0 until a seek trick
      if (!isFinite(audio.duration) || audio.duration === 0) {
        const seekableEnd = (() => {
          try { return audio.seekable?.length ? audio.seekable.end(audio.seekable.length - 1) : null; } catch { return null; }
        })();
        const target = seekableEnd || 1e6;
        const onSeeked = () => {
          try {
            setDuration(audio.duration || 0);
            audio.currentTime = 0;
          } catch {}
          audio.removeEventListener("seeked", onSeeked);
        };
        audio.addEventListener("seeked", onSeeked);
        try { audio.currentTime = target; } catch {}
        return;
      }
      setDuration(audio.duration);
    };

    const handleLoaded = () => { fixDurationIfNeeded(); };

    const handleTimeUpdate = () => {
      if (!audio.duration) return;
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    const handlePause = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);

    audio.addEventListener("loadedmetadata", handleLoaded);
    audio.addEventListener("durationchange", handleLoaded);
    audio.addEventListener("loadeddata", handleLoaded);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("play", handlePlay);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoaded);
      audio.removeEventListener("durationchange", handleLoaded);
      audio.removeEventListener("loadeddata", handleLoaded);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("play", handlePlay);
    };
  }, [src]);

  useEffect(() => {
    const pauseOthers = (event) => {
      if (event.detail?.index === index) return;
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
    window.addEventListener("create-panel-audio-play", pauseOthers);
    return () => window.removeEventListener("create-panel-audio-play", pauseOthers);
  }, [index]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      window.dispatchEvent(
        new CustomEvent("create-panel-audio-play", { detail: { index } })
      );
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {
          setIsPlaying(false);
        });
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;

    const nextProgress = Number(event.target.value);
    const nextTime = (nextProgress / 100) * audio.duration;
    audio.currentTime = nextTime;
    setProgress(nextProgress);
  };

  return (
    <div className="inline-audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        className="audio-play-button"
        onClick={togglePlay}
        aria-label={isPlaying ? "Pause voice note" : "Play voice note"}
      >
        {isPlaying ? <FaPause /> : <FaPlay />}
      </button>
      <div className="audio-track">
        <input
          type="range"
          min="0"
          max="100"
          value={progress}
          onChange={handleSeek}
          className="audio-progress"
          aria-label="Seek voice note"
        />
        <div className="audio-time">
          <span>{formattedTime(currentTime)}</span>
          <span>{formattedTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}
